import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import BiteDBAPI from '../../../api/BiteDBAPI';
import { GlobalContext } from '../../../providers/GlobalProvider';
import { IColumnHeader } from 'interfaces/IFileInterfaces';

interface IAddExportTemplateModalProps {
  open: boolean;
  handleClose: () => void;
  databaseId: string;
  title?: string;
  handleRefresh?: () => void;
}

const defaultColumnHeaders: IColumnHeader[] = [
  { label: 'Product', value: 'name', isDisplayed: true },
  { label: 'Description', value: 'description', isDisplayed: true },
  { label: 'SKU', value: 'model_number', isDisplayed: true },
  { label: 'HTS Code', value: 'hts_code', isDisplayed: true },
  { label: 'Quantity', value: 'quantity', isDisplayed: true },
  { label: 'Value', value: 'product_value', isDisplayed: true },
  { label: 'Country of Origin', value: 'country_of_origin', isDisplayed: true },
  { label: 'Category', value: 'category', isDisplayed: true },
  { label: 'Duty Cost', value: 'dutyCost', isDisplayed: true },
];

const AddExportTemplateModal: React.FC<IAddExportTemplateModalProps> = ({
  open,
  handleClose,
  databaseId,
  title = 'Add Export Template',
  handleRefresh,
}) => {
  useEffect(() => {
    if (databaseId) {
      BiteDBAPI.getUniqueAdditionalColumnKeysByDatabaseId(databaseId).then(
        (response) => {
          if (response?.success && response?.result) {
            setHeaderColumns(
              defaultColumnHeaders.concat(
                response.result.map((h: string) => {
                  return { label: h, value: h, isDisplayed: false };
                }),
              ),
            );
          }
        },
      );
    }
  }, [databaseId]);

  const { cur_user } = useContext(GlobalContext);
  const [isSaving, setIsSaving] = useState(false);

  const [templateName, setTemplateName] = useState('');
  const [exportType, setExportType] = useState('csv');
  const [headerColumns, setHeaderColumns] = useState<Array<IColumnHeader>>([]);
  const [draggingHeaderIndex, setDraggingHeaderIndex] = useState<number | null>(
    null,
  );

  const handleColumnDragStart = (index: number) => {
    setDraggingHeaderIndex(index);
  };

  const handleColumnDrop = (targetIndex: number) => {
    if (draggingHeaderIndex === null) {
      return;
    }
    const dragged = headerColumns.at(draggingHeaderIndex);
    if (dragged) {
      const nextHeaders = headerColumns.filter(
        (h) => h.value !== dragged.value,
      );
      nextHeaders.splice(targetIndex, 0, dragged);
      setHeaderColumns(nextHeaders);
    }
    setDraggingHeaderIndex(null);
  };

  const handleToggleColumnHeader = (index: number) => {
    const newHeaderColumns = headerColumns.map((h, ind) => {
      if (index === ind) {
        h.isDisplayed = !h.isDisplayed;
      }
      return h;
    });
    setHeaderColumns(newHeaderColumns);
  };

  const resetForm = () => {
    setTemplateName('');
    setExportType('csv');
    BiteDBAPI.getUniqueAdditionalColumnKeysByDatabaseId(databaseId).then(
      (response) => {
        if (response?.success && response?.result) {
          setHeaderColumns(
            defaultColumnHeaders.concat(
              response.result.map((h: string) => {
                return { label: h, value: h, isDisplayed: false };
              }),
            ),
          );
        }
      },
    );
  };

  const closeWithReset = () => {
    resetForm();
    handleClose();
  };

  const handleCreateTemplate = async () => {
    if (isSaving) {
      return;
    }

    const groupId = cur_user?.selected_team?.id;
    if (!groupId) {
      enqueueSnackbar('No team selected. Please select a team and try again.', {
        variant: 'error',
      });
      return;
    }

    if (!templateName.trim()) {
      enqueueSnackbar('Template name is required.', { variant: 'error' });
      return;
    }

    try {
      setIsSaving(true);
      const response = await BiteDBAPI.createExternalTemplate({
        group_id: groupId,
        name: templateName.trim(),
        export_type: exportType,
        headers: headerColumns,
      });

      if (response?.success) {
        enqueueSnackbar('Export template added successfully.', {
          variant: 'success',
        });
        closeWithReset();
        handleRefresh?.();
      } else {
        enqueueSnackbar('Error adding export template.', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error adding export template:', error);
      enqueueSnackbar('Error adding export template.', { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      className='dialog_box'
      open={open}
      onClose={closeWithReset}
      maxWidth='md'
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: 'var(--bg-colors)',
          borderRadius: '0.5rem',
          maxWidth: '800px',
          width: '90%',
          margin: '0 auto',
        },
      }}
    >
      <DialogTitle
        id='export-template-modal-title'
        className='modal-header-content modal-title'
      >
        {title}
        <div
          className='audit-add-modal-head-cont-icon'
          onClick={closeWithReset}
          style={{ cursor: 'pointer' }}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='18'
            height='18'
            viewBox='0 0 18 18'
            fill='none'
          >
            <path
              d='M1.5 1.5L16.5 16.5M16.5 1.5L1.5 16.5'
              stroke='white'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>
      </DialogTitle>
      <DialogContent className='dialog-content-base'>
        <div className='audit-add-modal-cont-body-frame'>
          <div className='audit-add-modal-cont-body-frame-form'>
            <div className='audit-add-modal-cont-body-frame-form-row'>
              <label className='audit-add-modal-cont-body-frame-form-row-input-label-text'>
                Name <span className='required-star'>*</span>
              </label>
              <input
                type='text'
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder='Template name'
                className='audit-add-modal-cont-body-frame-form-input'
                style={{ width: '100%' }}
              />
            </div>

            <div className='audit-add-modal-cont-body-frame-form-row'>
              <label className='audit-add-modal-cont-body-frame-form-row-input-label-text'>
                Export type
              </label>
              <select
                className='audit-add-modal-cont-body-frame-form-input'
                style={{ width: '100%' }}
                value={exportType}
                onChange={(e) => setExportType(e.target.value)}
              >
                <option value='csv'>CSV</option>
                <option value='xlsx'>XLSX</option>
                <option value='pdf'>PDF</option>
                <option value='json'>JSON</option>
              </select>
            </div>

            <div className='audit-add-modal-cont-body-frame-form-row'>
              <Box display='flex' flexDirection='column' gap={3}>
                <Typography color='text.secondary' variant='body2'>
                  Drag and drop the headers to set the export order. Click any
                  columns you do not want to include.
                </Typography>
                <Card variant='outlined'>
                  <CardContent>
                    <Box
                      display='flex'
                      flexDirection='column'
                      gap={2}
                      onDragOver={(event: any) => event.preventDefault()}
                    >
                      {headerColumns.map((header, index) => (
                        <Box
                          key={`${header.value}-${index}`}
                          display='flex'
                          justifyContent='space-between'
                          alignItems='center'
                          p={1.5}
                          borderRadius={1}
                          border='1px solid'
                          borderColor='divider'
                          bgcolor='background.paper'
                          draggable
                          onDragStart={() => handleColumnDragStart(index)}
                          onDragOver={(event: any) => event.preventDefault()}
                          onDrop={() => handleColumnDrop(index)}
                          onClick={() => handleToggleColumnHeader(index)}
                          sx={{
                            cursor: 'grab',
                            border: header.isDisplayed
                              ? '1px solid'
                              : '1px solid transparent',
                            borderColor: header.isDisplayed
                              ? 'primary.main'
                              : 'divider',
                            backgroundColor: 'primary.50',
                            opacity:
                              draggingHeaderIndex === index ? 0.6 : undefined,
                          }}
                        >
                          <Box>
                            <Typography fontWeight={600}>
                              {header.label}
                            </Typography>
                            <Typography
                              variant='caption'
                              color='text.secondary'
                            >
                              {header.value}
                            </Typography>
                          </Box>
                          <Box>
                            {header.isDisplayed && (
                              <CheckCircle
                                sx={{ fill: 'var(--action-color)' }}
                              />
                            )}
                          </Box>
                        </Box>
                      ))}

                      {headerColumns.length === 0 && (
                        <Box
                          display='flex'
                          justifyContent='center'
                          alignItems='center'
                          py={4}
                          borderRadius={1}
                          border='1px dashed'
                          borderColor='divider'
                          color='text.secondary'
                        >
                          <Typography variant='body2'>
                            No columns selected. Add at least one header to
                            continue.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </div>

            <div className='audit-modal-cont-footer'>
              <div className='audit-modal-cont-footer-content'>
                <div
                  className='audit-modal-cont-footer-content-btn-cancel'
                  onClick={closeWithReset}
                >
                  <div className='audit-modal-cont-footer-content-btn-text'>
                    Cancel
                  </div>
                </div>
                <div className='audit-modal-cont-footer-content-btn-upload'>
                  <div
                    className={`audit-modal-cont-footer-content-btn-text ${
                      isSaving ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    onClick={!isSaving ? handleCreateTemplate : undefined}
                    style={{ color: '#FFF' }}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddExportTemplateModal;
